/**
 * TP-Link Omada router catalog.
 *
 * The Omada SDN ecosystem spans SOHO VPN gateways (ER605) through
 * dual-WAN prosumer routers (ER7206) to enterprise 10G models
 * (ER8411). The newer ER706W and ER706W-4G variants add Wi-Fi 6 +
 * optional LTE backup. All entries verified against tp-link.com
 * official datasheet PDFs and omadanetworks.com product pages.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const TPLINK_OMADA_ROUTERS: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // SOHO / small business
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.omadanetworks.com/us/business-networking/omada-router-wired-router/er605/
  // SRC: https://www.tp-link.com/au/business-networking/omada-sdn-switch/er605/
  // SRC: https://www.broadbandbuyer.com/products/42647-tp-link-er605/specifications/
  //      ER605 (TL-R605): 5× Gigabit RJ45 (1 fixed WAN + 2 WAN/LAN with
  //      load balance + 2 fixed LAN), 1× USB WAN for mobile broadband,
  //      20× LAN-to-LAN IPsec + 16× OpenVPN + 16× L2TP + 16× PPTP,
  //      IPsec throughput 248.04 Mbps (ESP-SHA2-AES256), 150 k
  //      concurrent sessions, Omada SDN cloud-managed.
  {
    id: 'tplink-er605',
    manufacturer: 'TP-Link',
    model: 'ER605 (TL-R605)',
    category: 'router',
    tagline:
      'Omada ER605 — SOHO VPN gateway, 5× GbE + USB-WAN, 248 Mbps IPsec, 150k sessions',
    verified: true,
    datasheetUrl:
      'https://www.omadanetworks.com/us/business-networking/omada-router-wired-router/er605/',
    specs: {
      wanPorts: 3, // 1 fixed WAN + 2 WAN/LAN
      lanPorts: 4, // 2 fixed LAN + 2 WAN/LAN flippable
      lanPortSpeed: '1G',
      throughputGbps: 0.25, // IPsec throughput from datasheet
      wifi: 'none',
      cellular: 'none',
      formFactor: 'desktop',
      management: 'cloud', // Omada SDN
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Mid-range / prosumer — dual-WAN, SFP uplink
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.tp-link.com/us/business-networking/omada-sdn-router/er7206/v1/
  // SRC: https://static.tp-link.com/2021/202103/20210304/ER7206(UN)1.0_Datasheet.pdf
  //      ER7206 (TL-ER7206): 1× Gigabit SFP WAN + 1× Gigabit RJ45 WAN
  //      + 2× Gigabit WAN/LAN + 2× Gigabit RJ45 LAN (6 ports total,
  //      up to 4 WAN), 100× IPsec / 50× OpenVPN / 50× L2TP / 50×
  //      PPTP VPN with IPSec/SSL protocols, 64-bit dual-core CPU,
  //      512 MB DDR3, rack-mount option.
  {
    id: 'tplink-er7206',
    manufacturer: 'TP-Link',
    model: 'ER7206 (TL-ER7206)',
    category: 'router',
    tagline:
      'Omada ER7206 — 1× SFP + 5× GbE, 4-WAN load balance, 100× IPsec, 64-bit dual-core',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/omada-sdn-router/er7206/v1/',
    specs: {
      wanPorts: 4, // up to 4 WAN configurable
      lanPorts: 5, // 2 fixed LAN + 2 WAN/LAN + 1 RJ45 WAN that can be LAN
      lanPortSpeed: '1G',
      sfpPorts: 1,
      wifi: 'none',
      cellular: 'none',
      formFactor: '1U',
      management: 'cloud',
    },
  },

  // SRC: https://www.omadanetworks.com/us/business-networking/omada-router-wifi-router/er706w/
  // SRC: https://www.amazon.com/TP-Link-ER706W-Detachable-Integrated-Protection/dp/B0D2M2TMBC
  //      ER706W: Wi-Fi 6 AX3000 (2402 Mbps 5 GHz + 574 Mbps 2.4 GHz =
  //      3 Gbps combined), 6× Gigabit Ethernet (1 SFP + 5 RJ45),
  //      1× USB 3.0 for storage + LTE backup dongle, 10× PPTP/L2TP
  //      tunnels + 5× OpenVPN servers, IPSec / SSL / WireGuard / PPTP /
  //      L2TP VPN, integrated IDS/IPS with 4000+ signature rules,
  //      3× external detachable antennas, Omada SDN managed.
  {
    id: 'tplink-er706w',
    manufacturer: 'TP-Link',
    model: 'ER706W',
    category: 'router',
    tagline:
      'Omada ER706W — Wi-Fi 6 AX3000 + 1× SFP + 5× GbE, USB-LTE backup, IDS/IPS',
    verified: true,
    datasheetUrl:
      'https://www.omadanetworks.com/us/business-networking/omada-router-wifi-router/er706w/',
    specs: {
      wanPorts: 1,
      lanPorts: 5,
      lanPortSpeed: '1G',
      sfpPorts: 1,
      throughputGbps: 3,
      wifi: 'wifi6',
      cellular: 'lte-cat4', // via optional USB LTE dongle
      formFactor: 'desktop',
      management: 'cloud',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Enterprise / 10G rack
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.tp-link.com/us/business-networking/omada-sdn-router/er8411/v1/
  // SRC: https://static.tp-link.com/upload/product-overview/2023/202303/20230316/ER8411(UN)1.0_Datasheet.pdf
  //      ER8411: 2× 10GE SFP+ ports (1× WAN + 1× WAN/LAN), up to 10
  //      WAN total (fibre + RJ45) with load balance, IPSec / SSL /
  //      PPTP / L2TP / WireGuard / GRE / OpenVPN, rack-mountable 1U.
  //      Operating temp 0..+40 °C / storage -40..+70 °C, 10-90 % RH
  //      non-condensing. Zero-Touch Provisioning + Omada SDN cloud.
  {
    id: 'tplink-er8411',
    manufacturer: 'TP-Link',
    model: 'ER8411',
    category: 'router',
    tagline:
      'Omada ER8411 — 10G enterprise VPN gateway, 2× SFP+ 10GE, up to 10 WAN, ZTP',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/omada-sdn-router/er8411/v1/',
    specs: {
      wanPorts: 10, // up to 10 WAN configurable
      lanPorts: 4, // RJ45 LAN ports — datasheet shows total port mix
      lanPortSpeed: '10G',
      sfpPorts: 2, // both SFP+ 10G
      wifi: 'none',
      cellular: 'none',
      formFactor: '1U',
      management: 'cloud',
    },
  },
];
